import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useRef, useState } from 'react';
import { cn } from '../utils/cn.js';
/**
 * Shared class for items inside a `<Menu>` panel. Apply it to the `<Link>` /
 * `<button>` elements the consumer passes as children so every menu item looks
 * the same across apps.
 */
export const MENU_ITEM_CLASS = 'block w-full text-left px-4 py-2 font-body text-sm text-ink no-underline ' +
    'hover:bg-forest/5 focus:outline-none focus-visible:bg-forest/5';
/**
 * A small accessible dropdown for a header user-menu (name → Profile / Sign
 * out). Disclosure pattern, not an ARIA `menu`: the panel is a plain container
 * of focusable links/buttons, so Tab moves between them naturally and no
 * arrow-key roving is required. Closes on Escape (focus returns to the
 * trigger), on an outside click, and after any item inside is clicked.
 *
 * Router-agnostic + hook-free so it can live in @truecare/ui: the consumer
 * supplies the items (a TanStack `<Link>`, a sign-out `<button>`, …).
 */
export function Menu({ trigger, label, align = 'right', children, className }) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const panelId = useId();
    useEffect(() => {
        if (!open)
            return;
        const onPointerDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                setOpen(false);
                triggerRef.current?.focus();
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);
    return (_jsxs("div", { ref: rootRef, className: cn('relative', className), children: [_jsxs("button", { ref: triggerRef, type: "button", "aria-haspopup": "true", "aria-expanded": open, "aria-controls": open ? panelId : undefined, "aria-label": label, onClick: () => setOpen((o) => !o), className: cn('inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-body text-sm text-ink-muted', 'hover:text-forest focus:outline-none focus-visible:ring-2 focus-visible:ring-forest/40'), children: [trigger, _jsx("svg", { "aria-hidden": "true", viewBox: "0 0 20 20", fill: "currentColor", className: cn('h-4 w-4 transition-transform', open && 'rotate-180'), children: _jsx("path", { fillRule: "evenodd", d: "M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z", clipRule: "evenodd" }) })] }), open && (_jsx("div", { id: panelId, onClick: () => setOpen(false), className: cn('absolute z-40 mt-1 min-w-44 overflow-hidden rounded-md border border-ink-soft bg-paper py-1', align === 'right' ? 'right-0' : 'left-0'), children: children }))] }));
}
//# sourceMappingURL=Menu.js.map